import { createPermutation, createRandom } from './random';

/**
 * Seeded gradient and cellular noise.
 *
 * Every generator is built from an explicit seed so that a design always
 * reproduces the same relief — a saved configuration has to print identically
 * tomorrow.
 */

const GRAD2 = [
  [1, 1],
  [-1, 1],
  [1, -1],
  [-1, -1],
  [1, 0],
  [-1, 0],
  [0, 1],
  [0, -1],
] as const;

const F2 = 0.5 * (Math.sqrt(3) - 1);
const G2 = (3 - Math.sqrt(3)) / 6;

export interface Noise2D {
  /** Returns a value in roughly `-1 .. 1`. */
  (x: number, y: number): number;
}

/** Classic 2D simplex noise. */
export function createSimplexNoise(seed: number): Noise2D {
  const perm = createPermutation(seed);

  return function noise(xin: number, yin: number): number {
    const s = (xin + yin) * F2;
    const i = Math.floor(xin + s);
    const j = Math.floor(yin + s);
    const t = (i + j) * G2;

    const x0 = xin - (i - t);
    const y0 = yin - (j - t);

    const i1 = x0 > y0 ? 1 : 0;
    const j1 = x0 > y0 ? 0 : 1;

    const x1 = x0 - i1 + G2;
    const y1 = y0 - j1 + G2;
    const x2 = x0 - 1 + 2 * G2;
    const y2 = y0 - 1 + 2 * G2;

    const ii = i & 255;
    const jj = j & 255;

    let total = 0;

    let t0 = 0.5 - x0 * x0 - y0 * y0;
    if (t0 > 0) {
      const g = GRAD2[perm[ii + perm[jj]] & 7];
      t0 *= t0;
      total += t0 * t0 * (g[0] * x0 + g[1] * y0);
    }

    let t1 = 0.5 - x1 * x1 - y1 * y1;
    if (t1 > 0) {
      const g = GRAD2[perm[ii + i1 + perm[jj + j1]] & 7];
      t1 *= t1;
      total += t1 * t1 * (g[0] * x1 + g[1] * y1);
    }

    let t2 = 0.5 - x2 * x2 - y2 * y2;
    if (t2 > 0) {
      const g = GRAD2[perm[ii + 1 + perm[jj + 1]] & 7];
      t2 *= t2;
      total += t2 * t2 * (g[0] * x2 + g[1] * y2);
    }

    return 70 * total;
  };
}

export interface FbmOptions {
  octaves: number;
  /** Amplitude factor between octaves. */
  gain: number;
  /** Frequency factor between octaves. */
  lacunarity: number;
  /** Folds each octave around zero, producing sharp organic ridges. */
  ridged: boolean;
}

/**
 * Fractal Brownian motion over a base noise. The result is normalised by the
 * accumulated amplitude, so changing the octave count does not change the range.
 */
export function createFbm(base: Noise2D, options: FbmOptions): Noise2D {
  const octaves = Math.max(1, Math.round(options.octaves));
  const { gain, lacunarity, ridged } = options;

  return function fbm(x: number, y: number): number {
    let amplitude = 1;
    let frequency = 1;
    let sum = 0;
    let norm = 0;

    for (let octave = 0; octave < octaves; octave += 1) {
      const raw = base(x * frequency, y * frequency);
      const value = ridged ? 1 - 2 * Math.abs(raw) : raw;
      sum += value * amplitude;
      norm += amplitude;
      amplitude *= gain;
      frequency *= lacunarity;
    }

    return norm === 0 ? 0 : sum / norm;
  };
}

export interface CellularSample {
  /** Distance to the nearest feature point. */
  f1: number;
  /** Distance to the second nearest feature point. */
  f2: number;
}

/**
 * Worley / cellular noise on a jittered unit grid. `f2 - f1` traces the cell
 * borders, which is what gives the organic "cracked stone" reliefs their edges.
 */
export function createCellularNoise(seed: number): (x: number, y: number) => CellularSample {
  const perm = createPermutation(seed);
  const jitterRandom = createRandom(seed ^ 0x9e3779b9);

  // Precompute one jittered feature point per permutation slot. Looking the
  // offset up by hash keeps the generator stateless during sampling.
  const offsets = new Float32Array(512);
  for (let i = 0; i < 256; i += 1) {
    offsets[i * 2] = jitterRandom();
    offsets[i * 2 + 1] = jitterRandom();
  }

  return function cellular(x: number, y: number): CellularSample {
    const cellX = Math.floor(x);
    const cellY = Math.floor(y);

    let f1 = Infinity;
    let f2 = Infinity;

    for (let dy = -1; dy <= 1; dy += 1) {
      for (let dx = -1; dx <= 1; dx += 1) {
        const gx = cellX + dx;
        const gy = cellY + dy;
        const hash = perm[(gx & 255) + perm[gy & 255]] & 255;
        const px = gx + offsets[hash * 2];
        const py = gy + offsets[hash * 2 + 1];
        const ddx = px - x;
        const ddy = py - y;
        const dist = Math.sqrt(ddx * ddx + ddy * ddy);
        if (dist < f1) {
          f2 = f1;
          f1 = dist;
        } else if (dist < f2) {
          f2 = dist;
        }
      }
    }

    return { f1, f2 };
  };
}
